import React, { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import { FixAction, Requirement } from './types';
import { useSharedStyles } from './styles/shared';

interface FixOptionsScreenProps {
  requirement: Requirement;
  onApply: (selectedActions: FixAction[]) => void;
  onBack: () => void;
}

const useStyles = makeStyles(theme => ({
  headerBox: {
    marginBottom: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
  },
  grid: {
    marginBottom: theme.spacing(4),
  },
  card: {
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[2],
    },
    // The checkbox inside is what takes keyboard focus; show it on the card.
    '&:focus-within': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  cardContentBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  checkbox: {
    marginTop: theme.spacing(-1),
  },
  optionTextBox: {
    flex: 1,
  },
  optionLabel: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerButtons: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  cardSelected: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
  cardUnselected: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
}));

const FixOptionsScreen: React.FC<FixOptionsScreenProps> = ({
  requirement,
  onApply,
  onBack,
}) => {
  const shared = useSharedStyles();
  const classes = useStyles();
  const { t } = useTranslation('requirement-doctor');

  const OPTION_IDS = [
    'spell',
    'split',
    'testability',
    'comprehensive',
  ] as const;

  // Only selection state is stored; labels/descriptions are derived from t()
  // on each render so language switches are reflected immediately.
  const [selectedSet, setSelectedSet] = useState<Set<string>>(new Set());

  const options: FixAction[] = useMemo(
    () =>
      OPTION_IDS.map((id, i) => ({
        id,
        label: t(`s5.options.${i}.label`),
        description: t(`s5.options.${i}.description`),
        selected: selectedSet.has(id),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, selectedSet],
  );

  const handleToggle = (id: string) => {
    setSelectedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectedCount = selectedSet.size;
  const isValid = selectedCount > 0;

  const handleApply = () => {
    if (!isValid) return;
    onApply(options.filter(o => o.selected));
  };

  return (
    <Box className={shared.root}>
      <Box className={classes.headerBox}>
        <Typography variant="h5" className={classes.title}>
          {t('s5.title')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t('s5.description')}
        </Typography>
      </Box>

      <Alert severity="info" className={shared.mb3}>
        <strong>{t('s5.requirementId')}:</strong> {requirement.id} |{' '}
        <strong>{t('s5.requirementText')}:</strong>{' '}
        {requirement.text.length > 60
          ? `${requirement.text.substring(0, 60)}...`
          : requirement.text}
      </Alert>

      <Grid container spacing={2} className={classes.grid}>
        {options.map(option => (
          <Grid item xs={12} sm={6} key={option.id}>
            <Card
              className={`${classes.card} ${
                option.selected ? classes.cardSelected : classes.cardUnselected
              }`}
              onClick={() => handleToggle(option.id)}
            >
              <CardContent>
                <Box className={classes.cardContentBox}>
                  {/* A real control, so the keyboard reaches it with Tab and
                      toggles it with Space. Its own click stops here: the card
                      toggles on click too, and both firing would cancel out. */}
                  <Checkbox
                    checked={option.selected}
                    onChange={() => handleToggle(option.id)}
                    onClick={e => e.stopPropagation()}
                    className={classes.checkbox}
                    inputProps={{
                      'aria-labelledby': `fix-option-${option.id}-label`,
                      'aria-describedby': `fix-option-${option.id}-description`,
                    }}
                  />
                  <Box className={classes.optionTextBox}>
                    <Typography
                      id={`fix-option-${option.id}-label`}
                      variant="subtitle1"
                      className={classes.optionLabel}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      id={`fix-option-${option.id}-description`}
                      variant="body2"
                      color="textSecondary"
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box className={classes.footer}>
        <Typography variant="body2" color="textSecondary">
          {t('s5.messages.selected', { count: selectedCount })}
        </Typography>
        <Box className={classes.footerButtons}>
          <Button variant="outlined" onClick={onBack}>
            {t('s5.buttons.previous')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            disabled={!isValid}
          >
            {t('s5.buttons.apply')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FixOptionsScreen;
